import {photoLocation} from '../config/photoLocation.js';
import type {SonataDatabase} from './db.js';
import type {JournalPolicy,JournalPhoto,PhotoAudience} from './journalRepository.js';
export interface PreparedPhoto {
 photo_id:string;farm_id:string;audience:PhotoAudience;caption:string;
 upload:Record<string,unknown>&{file_base64:string;uploaded_at:string};
 link?:{entity_type:string;entity_id:string;record_link_id:string};
 operation_id:string;
}
export interface SavedPhoto extends PreparedPhoto {
 created_at:string;author_name:string;stage:'upload'|'link'|'publication';status:'pending'|'blocked';error?:string;
 publication?:Record<string,unknown>;result?:JournalPhoto;
}
type Request=<T>(path:string,method?:string,body?:unknown)=>Promise<T>;
const status=(error:unknown)=>(error as any)?.status||0;
export class PhotoOutbox {
 private flushing:Promise<void>|null=null;
 constructor(private database:SonataDatabase,private request:Request,private policy:()=>JournalPolicy,private current:()=>boolean,private online=()=>navigator.onLine,private changed=()=>{}){}
 private check(scope=this.policy().scope){if(!this.current()||scope!==this.policy().scope)throw new Error('Account or photo access changed. Your saved photo remains in its original account.');}
 private allowed(photo:PreparedPhoto){const p=this.policy();return p.active&&p.canUpload!==false&&(p.admin||p.farms.includes(photo.farm_id))&&(photo.audience==='work'||p.canComment);}
 async all(){const p=this.policy();this.check();const queue:SavedPhoto[]=(await this.database.workspaceMeta.get('photo-outbox'))?.value||[];this.check(p.scope);return queue;}
 async ownedFiles(){
  // Author-supplied bytes survive loss of property access. Do not project a
  // cached server response, target labels, links, or protected library media.
  return (await this.all()).map(photo=>({photo_id:photo.photo_id,created_at:photo.created_at,caption:photo.caption,
   file_name:String(photo.upload.file_name||'saved-photo.jpg'),file_base64:photo.upload.file_base64,
   status:photo.status,error:photo.error,can_retry:this.allowed(photo)}));
 }
 async reconcile(){
  const scope=this.policy().scope;this.check();await this.database.transaction('rw',this.database.workspaceMeta,async()=>{
   const queue=await this.all();this.check(scope);
   await this.database.workspaceMeta.put({key:'photo-outbox',value:queue.map(photo=>photo.status==='pending'&&!this.allowed(photo)?{...photo,status:'blocked',error:'Photo or property access changed. This photo was not sent.'}:photo)});
  });
 }
 async save(prepared:PreparedPhoto){
  const policy=this.policy();this.check();if(!this.allowed(prepared))throw new Error('Photo access is unavailable.');
  if(prepared.caption.length>2000||!prepared.upload.file_base64.startsWith('data:image/'))throw new Error('Choose a photo and a caption of at most 2000 characters.');
  // Preserve the exact compressed bytes and initial identifiers before the first
  // request. A reload never recompresses an already submitted image.
  await this.database.transaction('rw',this.database.workspaceMeta,async()=>{
   this.check(policy.scope);const queue=await this.all(),existing=queue.find(p=>p.photo_id===prepared.photo_id);
   if(existing){
    for(const key of ['upload','link','caption','audience','farm_id'] as const)if(JSON.stringify(existing[key])!==JSON.stringify(prepared[key]))throw new Error('This photo is already saved. Finish its pending upload before changing it.');
    return;
   }
   if(queue.reduce((total,p)=>total+p.upload.file_base64.length,0)+prepared.upload.file_base64.length>100*1024*1024)throw new Error('Saved photos are full. Connect to send them before adding more.');
   await this.database.workspaceMeta.put({key:'photo-outbox',value:[...queue,{...prepared,created_at:new Date().toISOString(),author_name:policy.name,stage:'upload',status:'pending'}]});
  });
  this.check(policy.scope);this.changed();await this.flush();
  const remaining=(await this.all()).find(p=>p.photo_id===prepared.photo_id);
  return {photo_id:prepared.photo_id,pending:!!remaining,error:remaining?.error};
 }
 private async update(id:string,scope:string,change:(row:SavedPhoto)=>SavedPhoto|null){
  await this.database.transaction('rw',this.database.workspaceMeta,async()=>{
   this.check(scope);const queue=await this.all();await this.database.workspaceMeta.put({key:'photo-outbox',value:queue.flatMap(p=>p.photo_id===id?[change(p)].filter(Boolean):[p])});
  });this.check(scope);this.changed();
 }
 async flush(){
  if(!this.online()||!this.current())return;if(this.flushing)return this.flushing;
  const scope=this.policy().scope;
  this.flushing=(async()=>{
   for(let photo of await this.all()){
    this.check(scope);if(photo.status!=='pending'||!this.allowed(photo))continue;
    try{
     if(photo.stage==='upload'){
      const uploaded=await this.request<JournalPhoto>('/scouting/photos/upload','POST',photo.upload);this.check(scope);
      const next:SavedPhoto={...photo,result:uploaded,stage:photo.link?'link':'publication',publication:{operation_id:photo.operation_id,revision:uploaded.publication_revision,work_photos:photo.audience==='work',nature_gallery:photo.audience==='nature',confirm_share:photo.audience==='nature',community_caption:photo.audience==='nature'?photo.caption:''}};
      await this.update(photo.photo_id,scope,()=>next);photo=next;
     }
     if(photo.stage==='link'){
      await this.request('/scouting/photos/'+encodeURIComponent(photo.photo_id)+'/links','POST',photo.link);this.check(scope);
      photo={...photo,stage:'publication'};await this.update(photo.photo_id,scope,()=>photo);
     }
     if(photo.stage==='publication'){
      await this.request('/journal/photos/'+encodeURIComponent(photo.photo_id)+'/publication','POST',photo.publication);this.check(scope);
      await this.update(photo.photo_id,scope,()=>null);
     }
    }catch(error){
     this.check(scope);
     if(![400,403,404,409,413].includes(status(error)))return;
     await this.update(photo.photo_id,scope,row=>({...row,status:'blocked',error:status(error)===409?'This photo changed. Review its saved version before retrying.':'This photo or its linked item is no longer available. The upload stopped.'}));
    }
   }
  })().finally(()=>{this.flushing=null;});return this.flushing;
 }
 async retry(id:string){
  const scope=this.policy().scope,photo=(await this.all()).find(p=>p.photo_id===id);if(!photo||!this.allowed(photo))throw new Error('Photo access is unavailable.');
  await this.update(id,scope,p=>({...p,status:'pending',error:undefined}));await this.flush();
 }
 async discard(id:string){const scope=this.policy().scope;this.check();await this.update(id,scope,()=>null);}
 async photos(farm:string,audience:PhotoAudience){
  const policy=this.policy(),queue=await this.all();this.check(policy.scope);return queue.filter(p=>p.farm_id===farm&&p.audience===audience&&(policy.admin||policy.farms.includes(farm))).map(p=>this.preview(p));
 }
 async contextPhotos(farm:string,type:'project'|'asset',id:string){
  const policy=this.policy(),queue=await this.all();this.check(policy.scope);
  if(!policy.active||(!policy.admin&&!policy.farms.includes(farm)))return [];
  return queue.filter(photo=>photo.farm_id===farm&&photo.audience==='work'&&photo.link?.entity_type===type&&photo.link.entity_id===id).map(photo=>this.preview(photo));
 }
 preview(p:SavedPhoto):JournalPhoto{
  this.check();if(!this.policy().active||(!this.policy().admin&&!this.policy().farms.includes(p.farm_id)))throw new Error('Property access is unavailable.');
  const blocked=p.status==='blocked'||!this.allowed(p);return {photo_id:p.photo_id,farm_id:p.farm_id,source:'Upload',created_by:this.policy().account,uploaded_at:p.upload.uploaded_at,
   taken_at:p.upload.taken_at as string|undefined,storage_url:blocked?'':p.upload.file_base64,image_url:blocked?'':p.upload.file_base64,
   photo_location:photoLocation(p.upload.exif_lat,p.upload.exif_lng),notes:p.caption,author_name:p.author_name,place_labels:[],work_photos:p.audience==='work',nature_gallery:p.audience==='nature',publication_revision:0,server_version:0,
   can_edit:false,can_moderate:false,can_comment:false,hidden:false,classification_status:'pending',pending:true,pending_error:p.error,audience:p.audience};
 }
}
